import React from 'react';
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { KnowledgeBase } from '@/types/supabase';

const formSchema = z.object({
  name: z.string().min(2, {
    message: "Knowledge Base name must be at least 2 characters.",
  }),
  description: z.string().optional(),
  isPrivate: z.boolean().optional()
});

interface KnowledgeBaseEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: z.infer<typeof formSchema>) => Promise<void>;
  knowledgeBase: KnowledgeBase | null;
}

const KnowledgeBaseEditDialog = ({
  open,
  onOpenChange,
  onSubmit,
  knowledgeBase
}: KnowledgeBaseEditDialogProps) => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: knowledgeBase?.name || "",
      description: knowledgeBase?.description || "",
      isPrivate: knowledgeBase?.is_private || false
    },
  });

  // Update form values when knowledgeBase changes
  React.useEffect(() => {
    if (knowledgeBase) {
      form.setValue("name", knowledgeBase.name);
      form.setValue("description", knowledgeBase.description || "");
      form.setValue("isPrivate", knowledgeBase.is_private);
    }
  }, [form, knowledgeBase]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden border-border/70 bg-background/95 p-0 shadow-2xl sm:max-w-[560px]">
        <DialogHeader className="border-b border-border/60 px-6 py-5">
          <DialogTitle>Edit Knowledge Base</DialogTitle>
          <DialogDescription>
            Edit the name and description of your knowledge base.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5 px-6 py-5">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="My Knowledge Base" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="A brief description of the knowledge base"
                      className="min-h-[120px] resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="isPrivate"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-xl border border-border/70 bg-muted/20 p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Private</FormLabel>
                    <FormDescription>
                      Make this knowledge base private
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <DialogFooter className="border-t border-border/60 px-0 pt-5">
              <Button type="submit">Update</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default KnowledgeBaseEditDialog;